import React from 'react';

import type { AlineSelection } from '@aline/common';

import { createPublishedTabScopedRegistry } from './tab-scoped-registry';

export interface EditorUiState {
  focused: boolean;
  selection: AlineSelection | null;
}

/**
 * Selection and focus change much more frequently than stable editor runtime
 * handles like the editor and DocumentViewer instances. Keeping them in a
 * separate tab-scoped store prevents every cursor move or focus update from
 * re-rendering all stable runtime consumers.
 */
const editorUiStateRegistry = createPublishedTabScopedRegistry<EditorUiState>({
  useTabIdErrorMessage: 'useEditorUiState must be used within an editor UI state scope.',
});
const PublishedEditorUiStateProvider = editorUiStateRegistry.Provider;

/**
 * The registry is the source of truth across tree boundaries, but hooks still
 * read through context. `EditorUiStateProvider` supplies this context inside
 * Slate, while `EditorUiStateScope` supplies the same context for global UI
 * like the right drawer.
 */
export const EditorUiStateScope = editorUiStateRegistry.Scope;

export function EditorUiStateProvider({
  children,
  tabId,
  uiState,
}: {
  children: React.ReactNode;
  tabId: UUID;
  uiState: EditorUiState;
}) {
  return (
    <PublishedEditorUiStateProvider state={uiState} tabId={tabId}>
      {children}
    </PublishedEditorUiStateProvider>
  );
}

export function useMaybeEditorUiState(): EditorUiState | null {
  return editorUiStateRegistry.useMaybeState();
}

export function useEditorUiState(): EditorUiState {
  const uiState = useMaybeEditorUiState();
  if (!uiState) throw new Error('useEditorUiState must be used within an editor UI state scope.');
  return uiState;
}
